import { Anchor, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { PaperRowControl } from '../../components/ThePaperItself';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { formatMoney } from '../../lib/format';
import { DeliveryDetailModal } from './DeliveryDetail';
import { ReceiveDeliveryForm } from './ReceiveDeliveryForm';

const RECEIPTS_PATH = '/admin/v1/suppliers/receipts';

/**
 * Everything that has come through the door, and the only place a delivery can be written down.
 *
 * The list matters as much as the button. "Did anyone enter the Madeyso order?" is asked most
 * mornings, and before this the answer lived nowhere a person could look: deliveries existed only
 * as something the shipping-over screen asked you to pick from, which is a list of things you had
 * no way to create.
 *
 * Not a CrudSection form, because a delivery is a bill AND a list of goods AND a shelf they went
 * onto, and the generic form offers one value per field.
 */
export function DeliveriesSection({ locationId }: { locationId?: string }) {
  const queryClient = useQueryClient();
  const currency = useBaseCurrency();
  const [recording, setRecording] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(RECEIPTS_PATH) });

  return (
    <>
      <CrudSection
        title="Deliveries"
        description="What has actually arrived, newest first. Recording one puts the goods on the shelf, brings the cost per unit up to date, and opens the supplier's bill. What it cost to bring a mainland order across is recorded separately under Shipping over. Written down wrong? Open it and correct it — the shelf, the bill and the order all follow, and what was typed at the door is kept."
        listPath={RECEIPTS_PATH}
        emptyMessage="No deliveries recorded yet. Record the first one when a supplier drops an order off."
        actions={
          <Button size="sm" onClick={() => setRecording(true)}>
            Record a delivery
          </Button>
        }
        columns={[
          {
            accessor: 'supplierName',
            title: 'Who delivered it',
            render: (r) => {
              const number = typeof r.invoiceNumber === 'string' ? r.invoiceNumber.trim() : '';
              return (
                <Stack gap={0}>
                  <Group gap={6} wrap="nowrap">
                    {/* The name is the way in, the way a supplier's name is on the supplier list.
                        What was on a delivery had no screen at all before this. */}
                    <Anchor
                      component="button"
                      type="button"
                      fw={600}
                      ta="left"
                      onClick={() => setOpening(String(r.id))}
                    >
                      {String(r.supplierName ?? 'Unnamed supplier')}
                    </Anchor>
                    {/* Only ever on rows written before this screen existed, or by the assistant.
                        A draft is not counted in stock, which is worth seeing rather than assuming. */}
                    {r.status === 'draft' && (
                      <Badge size="xs" variant="light" color="gray">
                        Not confirmed yet
                      </Badge>
                    )}
                    {/* One row per delivery, always: the one it replaced is not on this list, it
                        is reached from inside this one. The badge is there so a figure that moved
                        since somebody last looked has a visible reason. */}
                    {Boolean(r.correctsReceiptId) && (
                      <Badge size="xs" variant="light" color="blue">
                        Corrected
                      </Badge>
                    )}
                  </Group>
                  {/* Under the name rather than in a column of its own. Island vendors mostly do
                      not give an invoice number, so a column headed with one is a column of
                      dashes — ten rows deep here — on a list whose job is being checkable against
                      the paperwork. It is kept, because it is what gets matched to a statement,
                      and it is a qualifier on the delivery rather than a fact about it. The bill
                      lists reached the same conclusion first; see billSubject.tsx. */}
                  {number && (
                    <Text size="xs" c="dimmed">
                      {number}
                    </Text>
                  )}
                  {/* The delivery note the driver left, photographed at the door. This is the
                      list somebody scans when a supplier's statement does not match, and the
                      paper is the thing that settles it. */}
                  <PaperRowControl
                    documentId={r.sourceDocumentId as string | null}
                    attachPath={`${RECEIPTS_PATH}/${String(r.id)}/document`}
                    subject={`the delivery from ${String(r.supplierName ?? 'this supplier')}`}
                    kind="photo"
                    invalidate={[listKey(RECEIPTS_PATH)]}
                  />
                </Stack>
              );
            },
          },
          { accessor: 'receivedAt', title: 'Arrived', unit: 'datetime', noWrap: true },
          {
            accessor: 'lineCount',
            title: 'Items',
            render: (r) => {
              const count = typeof r.lineCount === 'number' ? r.lineCount : 0;
              return count === 0 ? <Text c="dimmed">Nothing on it</Text> : String(count);
            },
          },
          // In the house currency on purpose: this bar is billed in both lempiras and dollars, and
          // a column mixing the two is a column nobody can add up down the page.
          {
            accessor: 'goodsTotalBaseMinor',
            title: 'Goods came to',
            render: (r) => formatMoney(r.goodsTotalBaseMinor as number, currency),
          },
        ]}
      />

      {recording && (
        <ReceiveDeliveryForm locationId={locationId} onClose={() => setRecording(false)} onSaved={refresh} />
      )}

      {opening && <DeliveryDetailModal receiptId={opening} onClose={() => setOpening(null)} />}
    </>
  );
}
